// /api/hunt/agenda — the daily "what should I do today" view (EXCEL_SPRINTS PR5).

import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type { JobRow } from "./jobs";

export interface UpcomingInterview {
  application_id: string;
  role: string;
  company_name: string;
  scheduled_at: number;
  label: string | null;
}

export interface InProgressApplication {
  application_id: string;
  role: string;
  company_name: string;
  updated_at: number;
  // Optional: a cached pre-v6 agenda has none of these.
  expected_fit?: number | null;
  listing_active?: boolean;
  autofill?: boolean;
}

export interface StuckAgendaRow {
  application_id: string;
  role: string;
  company_name: string;
  age_days: number;
}

export interface WarmTouchDue {
  company_key: string;
  company_name: string;
  tier: number | null;
  applications: Array<{ application_id: string; role: string; status: string }>;
  contact_count: number;
  suggested_contact: { id: string; full_name: string; role: string | null } | null;
  last_touch: number | null;
}

export interface RecruiterReplyDue {
  message_id: string;
  subject: string | null;
  note: string | null;
  received_at: number;
  due_at: number;
  overdue: boolean;
  contact: { id: string; full_name: string; role: string | null; company_name: string | null } | null;
}

export interface HuntAgenda {
  generated_at: number;
  cached?: boolean;
  shortlist: JobRow[];
  shortlist_source: "centroid" | "cold_start" | "clef_fit" | null;
  stuck: StuckAgendaRow[];
  upcoming_interviews: UpcomingInterview[];
  in_progress: InProgressApplication[];
  approved_unsubmitted: InProgressApplication[];
  // Optional: a cached pre-v3 agenda can still be in flight during a deploy.
  warm_touches_due?: WarmTouchDue[];
  recruiter_replies_due?: RecruiterReplyDue[];
  summary: {
    shortlist: number;
    stuck: number;
    upcoming_interviews: number;
    in_progress: number;
    closed_preparing?: number;
    approved_unsubmitted: number;
    warm_touches_due?: number;
    recruiter_replies_due?: number;
  };
}

export function useHuntAgenda() {
  return useQuery({
    queryKey: ["hunt-agenda"],
    queryFn: () => api<HuntAgenda>("/api/hunt/agenda"),
    staleTime: 60_000,
  });
}
